import { t } from "@/locales/i18n";
import { isSessionExpiredError } from "./session-expired";

/** Api 请求失败：携带 HTTP 状态码与原始响应体，供调用方区分 403（无权限）/404 等。 */
export class RequestError extends Error {
  readonly code: string | null;
  readonly details: string | null;
  readonly status: number;
  readonly data: unknown;
  readonly sessionExpired: boolean;

  constructor(status: number, message: string, data?: unknown, code: string | null = null, details: string | null = null, sessionExpired = false) {
    super(message);
    this.name = "RequestError";
    this.code = code;
    this.details = details;
    this.status = status;
    this.data = data;
    this.sessionExpired = sessionExpired;
  }
}

/**
 * 把 axios 错误规整为可供业务消费的错误：
 * - 带响应体的（4xx/5xx）转成 {@link RequestError}，从 ABP 错误体（{error:{message,details}}）提取可读信息；
 * - 取消 / 网络错误等没有 response，原样返回。
 */
export function toRequestError(error: any): unknown {
  const response = error?.response;
  if (!response) {
    return error;
  }

  const { data, status } = response;
  if (isSessionExpiredError(error)) {
    return new RequestError(status, t("auth.sessionExpired.detail"), data, null, null, true);
  }
  const code = typeof data?.error?.code === "string" ? data.error.code.trim() || null : null;
  const message = typeof data?.error?.message === "string" ? data.error.message.trim() : "";
  const details = typeof data?.error?.details === "string" ? data.error.details.trim() : "";
  const validationMessages = Array.isArray(data?.error?.validationErrors)
    ? data.error.validationErrors
        .map((item: unknown) => (typeof (item as { message?: unknown })?.message === "string" ? (item as { message: string }).message.trim() : ""))
        .filter(Boolean)
    : [];
  const resolved = message || validationMessages.join("\n") || details || t("fallback.http.requestFailed", { status });
  return new RequestError(status, resolved, data, code, details || null);
}
